/* eslint-disable react/jsx-no-target-blank */
import type { ComponentType } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { Checks, WarningCircle, File, ImageSquare, VideoCamera } from 'src/common/icons';
import { Dropdown, Input, PageBody, SaveChangesBar, SettingsNavigation } from 'src/components';
import messages from './messages';
import styles from './styles.module.scss';
import { useAtom } from 'jotai';
import { storageAlertStatus, storageSettingsAtom } from 'src/shared/settings/storage';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { AWSRegions } from './s3Regions';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { PlanUpgradeBanner } from 'src/components/FeatureUnavailable/PlanUpgradeBanner';

/**
 * One card per storage class. The field keys map onto the flat
 * `storageSettings` shape returned by the settings API.
 */
type StorageClass = {
  key: string;
  label: MessageDescriptor;
  desc: MessageDescriptor;
  icon: ComponentType<{ className?: string }>;
  bucketField: string;
  folderField: string;
  geoField: string;
  okField: string;
};

const STORAGE_CLASSES: StorageClass[] = [
  {
    key: 'originals',
    label: messages.originalFiles,
    desc: messages.originalFilesDesc,
    icon: File,
    bucketField: 'originals_root',
    folderField: 'originals_root_folder',
    geoField: 'originals_root_geo',
    okField: 'originals_root_ok',
  },
  {
    key: 'derivatives',
    label: messages.imageConversions,
    desc: messages.imageConversionsDesc,
    icon: ImageSquare,
    bucketField: 'derivatives_root',
    folderField: 'derivatives_root_folder',
    geoField: 'derivatives_root_geo',
    okField: 'derivatives_root_ok',
  },
  {
    key: 'transcodes',
    label: messages.videoTranscodes,
    desc: messages.videoTranscodesDesc,
    icon: VideoCamera,
    bucketField: 'transcodes_root',
    folderField: 'transcodes_root_folder',
    geoField: 'transcodes_root_geo',
    okField: 'transcodes_root_ok',
  },
];

const regionOptions = AWSRegions.map((el) => ({
  key: el.region,
  value: `${el.name} · ${el.region}`,
}));

const regionLabel = (region?: string): string | undefined => {
  if (!region) return undefined;
  const match = AWSRegions.find((el) => el.region === region);
  return match ? `${match.name} · ${match.region}` : region;
};

export const StorageSettings = () => {
  const { isLoading } = useSettingsQuery();
  const [storageSettings, updateStorageSettings] = useAtom(storageSettingsAtom);
  const [storageAlert] = useAtom(storageAlertStatus);
  const [addons] = useAtom(addonsSettingsAtom);
  // BYO storage is a SCALE/Enterprise entitlement (plan-structure v2).
  const byosGated = !(addons?.BYO_STORAGE?.added && addons?.BYO_STORAGE?.enabled);

  const handleChange = (name: string, value: string) => {
    updateStorageSettings({ type: ACTION_TYPES.UPDATE_STATE, payload: { [name]: value } });
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.pageWrapper}>
      <SettingsNavigation />
      <SaveChangesBar source="storage" />

      <PageBody narrow>
        {byosGated && (
          <PlanUpgradeBanner
            gate={{ feature: 'byo_storage', requiredPlan: 'SCALE' }}
          />
        )}
        {storageAlert && (
          <div className={styles.errorAlert}>
            Invalid S3 bucket details provided. Please refer to the{' '}
            <a
              href="https://developers.filespin.io/reference/asset-storage"
              target="_blank"
              rel="noreferrer"
            >
              S3 Setup Docs
            </a>{' '}
            to prepare your S3 bucket for FileSpin
          </div>
        )}

        <p className={styles.intro}>
          <FormattedMessage {...messages.intro} />
        </p>

        <div className={styles.cards}>
          {STORAGE_CLASSES.map((cls) => {
            const Icon = cls.icon;
            const verified = Boolean(storageSettings[cls.okField]);
            const region = storageSettings[cls.geoField];

            return (
              <section key={cls.key} className={styles.card}>
                <header className={styles.cardHeader}>
                  <div className={styles.cardHeading}>
                    <span className={styles.cardIcon} aria-hidden>
                      <Icon />
                    </span>
                    <div>
                      <h3 className={styles.cardTitle}>
                        <FormattedMessage {...cls.label} />
                      </h3>
                      <p className={styles.cardDesc}>
                        <FormattedMessage {...cls.desc} />
                      </p>
                    </div>
                  </div>

                  <span
                    className={verified ? styles.statusOk : styles.statusPending}
                    data-test={`storage-status-${cls.key}`}
                  >
                    {verified ? <Checks /> : <WarningCircle />}
                    <FormattedMessage {...(verified ? messages.verified : messages.notVerified)} />
                  </span>
                </header>

                <div className={styles.fields}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>
                      <FormattedMessage {...messages.storageBucket} />
                    </span>
                    <Input
                      placeholder={messages.storageBucketPlaceholder.defaultMessage}
                      variant="secondary"
                      value={storageSettings[cls.bucketField] || ''}
                      onChange={(value: string) => handleChange(cls.bucketField, value)}
                    />
                  </div>

                  <div className={styles.fieldRow}>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        <FormattedMessage {...messages.storagePathPrefix} />
                      </span>
                      <Input
                        placeholder={messages.pathPrefixPlaceholder.defaultMessage}
                        variant="secondary"
                        value={storageSettings[cls.folderField] || ''}
                        onChange={(value: string) => handleChange(cls.folderField, value)}
                      />
                    </div>

                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        <FormattedMessage {...messages.storageLocation} />
                      </span>
                      <Dropdown
                        options={regionOptions}
                        className={styles.regionDropdown}
                        selectedKeys={[region]}
                        onClick={(value: string) => handleChange(cls.geoField, value)}
                      >
                        <span className={styles.regionValue}>
                          {regionLabel(region) || messages.regionPlaceholder.defaultMessage}
                        </span>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </PageBody>
    </div>
  );
};
